import React from 'react';
import Layout from '../../components/global/layout';
import SEO from '../../components/seo';
import PageBanner from '../../components/layouts/page-banner';
import {
  Box,
  Container,
  Grid,
  Link,
  makeStyles,
  Typography,
} from '@material-ui/core';
import LightMode from '../../components/global/light-mode';
import VerticalPadding from '../../components/global/vertical-padding';
import { SiteImage } from '../../components/site-image';
import headquartersImage from '../../images/marchnetworks-headquarters.jpg';
import MNLogo from '../../images/svg/marchnetworks-logo-colour.svg';

const useCaseStyles = makeStyles((theme) => ({
  root: {
    '& p': {
      lineHeight: 1.5,
    },
  },
  img: {
    height: 'auto',
    marginTop: 50,
    width: '100%',
    [theme.breakpoints.down('md')]: {
      maxWidth: 300,
    },
    [theme.breakpoints.only('lg')]: {
      maxWidth: 400,
    },
  },
}));

const MarchNetworks: React.FC = () => {
  const useCaseStyle = useCaseStyles();

  return (
    <Layout>
      <SEO
        title="Online Marketing Gets a Boost at March Networks"
        description="March Networks relies on Marvel &amp; Snap for the web-focused knowledge and expertise needed to maintain its public sites."
      />
      <PageBanner
        heading="Online Marketing Gets a Boost at March Networks"
        maxWidth={1200}
      />
      <LightMode>
        <VerticalPadding>
          <Container style={{ overflow: 'hidden' }}>
            <Grid container spacing={5} justifyContent="space-between">
              <Grid item xs={12} md={6} lg={5} style={{ textAlign: 'center' }}>
                <img
                  src={MNLogo}
                  alt="March Networks logo"
                  width="500"
                  height="106"
                  className={useCaseStyle.img}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  component="p"
                  className="lead"
                  gutterBottom={true}
                >
                  For{' '}
                  <Link
                    href="https://www.marchnetworks.com"
                    target="blank"
                    rel="noopener"
                  >
                    March Networks
                  </Link>
                  <sup>&copy;</sup>, an online presence has to go beyond static
                  webpages. The company’s websites are central hubs for all
                  digital marketing efforts, serving to both present the
                  company’s offerings and enable customer fulfillment,
                  retention, and support programs. March Networks works with
                  Marvel &amp; Snap to ensure all the required elements are
                  continuously optimized to meet marketing objectives.
                </Typography>
                <Typography
                  variant="h3"
                  component="h2"
                  style={{ marginTop: 40 }}
                >
                  Challenge
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  className={useCaseStyle.root}
                >
                  <p>
                    March Networks is a global provider of intelligent IP video
                    software and systems used for security applications in
                    banking, retail, transportation, commercial, and government
                    organizations in more than 50 countries. The company sells
                    products through an extensive dealer network, and relies on
                    a combination of multinational websites, a resource-rich
                    Partner Portal, email communications, and online marketing
                    programs to continually deliver the latest corporate news,
                    product information, and support tools to customers and
                    resellers.
                  </p>
                  <p>
                    “We have a small in-house team that sets the marketing
                    strategy, determines the direction, and develops the content
                    for everything we do online,” says Jacqueline Lawrence,
                    global leader, marketing at March Networks. “But there are a
                    lot of things in addition to strategy, direction, and
                    content that have to come together on a daily basis to make
                    all the pieces of our online presence work the way they need
                    to work. Website architecture, navigation, user experience,
                    design, functionality, development, coding, and best
                    practices are important and are really not our marketing
                    team’s areas of expertise. And we don’t have an inhouse team
                    of people who can look after our websites and focus on
                    day-to-day management and maintenance.”
                  </p>
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </LightMode>
      <Box bgcolor="primary.main" textAlign="center">
        <VerticalPadding>
          <Container>
            <Grid container justifyContent="center">
              <Grid item xs={12} lg={11} xl={10} style={{ color: 'white' }}>
                <Typography
                  variant="h2"
                  component="blockquote"
                  style={{ fontFamily: 'Visby CF', textTransform: 'unset' }}
                >
                  &ldquo;Marvel &amp; Snap is so tightly integrated with us that
                  we work with them every day. And it’s been a very positive
                  experience and a very good working relationship.&rdquo;
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </Box>
      <LightMode>
        <VerticalPadding>
          <Container style={{ lineHeight: 1.77, overflow: 'hidden' }}>
            <Grid container spacing={5} justifyContent="space-between">
              <Grid item xs={12} md={6} lg={5}>
                <SiteImage
                  src={headquartersImage}
                  objectFit="cover"
                  alt="March Networks Headquarters"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h3"
                  component="h2"
                  style={{ marginTop: 20 }}
                >
                  Solution
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  className={useCaseStyle.root}
                >
                  <p>
                    March Networks initially turned to Marvel &amp; Snap more
                    than 10 years ago for a website redesign. Since that time,
                    the company has worked with Marvel &amp; Snap on a second
                    website overhaul and the relationship has evolved. Today,
                    March Networks relies on Marvel &amp; Snap for the
                    web-focused knowledge and expertise needed to maintain its
                    public sites and password-protected partner portals, and
                    provide daily support for all of the marketing team’s online
                    marketing efforts.
                  </p>
                  <p>
                    “They have become an extension of our team and we rely on
                    them a lot,” Jacqueline says. “They provide the standard web
                    services for us and handle day-to-day maintenance. So, they
                    take care of ongoing updates we want to do in terms of
                    content or navigation or user experience. They handle
                    content, structural, and design changes that need to be made
                    on all of our sites, and they also help with larger
                    projects, like developing a new website or portal. They are
                    doing all that and making sure that it meets our standards,
                    as well as best practices.”
                  </p>
                  <p>
                    Jacqueline points to a digital asset management system as an
                    example of the custom web applications solutions Marvel
                    &amp; Snap provides. March Networks uses the system to
                    manage delivery of ungated collateral on the public sections
                    of its websites, gated digital content, such as software and
                    firmware downloads to customers, and sensitive pricing
                    sheets and partner program collateral to value-added
                    reseller partners.
                  </p>
                  <p>
                    “It was created as part of the second website redesign that
                    Marvel &amp; Snap did for us about six years ago and we are
                    still using it today,” Jacqueline says. “It’s a very complex
                    system. We put all of our digital assets into the system and
                    then use it to feed both the public websites and our
                    password-protected partner portals, which are only
                    accessible by March Networks certified partners. There are
                    different permissions for different parts of the world where
                    we have a presence. So, if you are in North America, you can
                    see one thing, in Europe you can see another, and so on.
                    Marvel &amp; Snap worked very closely with our IT team to
                    come up with the requirements, design a system that met
                    those requirements, and then build it so that it met all our
                    business needs.”
                  </p>
                </Typography>
                <Typography
                  variant="h3"
                  component="h2"
                  style={{ marginTop: 40 }}
                >
                  Result
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  className={useCaseStyle.root}
                >
                  <p>
                    Jacqueline emphasizes that Marvel &amp; Snap continues to be
                    an integrated part of the March Networks marketing team.
                  </p>
                  <p>
                    “It’s not like they are involved just here and there; they
                    are so tightly integrated with us that we work with them
                    every day. And it’s been a very positive experience and a
                    very good working relationship,” Jacqueline says. “Because
                    they’re so tightly integrated with us, they have so much
                    historical knowledge about how we work and how things have
                    evolved over the years. When we have something that needs to
                    get done, they can deal with it quickly because we speak the
                    same language now and they already know how we like to do
                    things.
                  </p>
                  <p>
                    “One of the things that’s interesting is that they will also
                    brainstorm with us. If we have ideas about what we would
                    like to do, they will share their expertise and opinions.
                    So, it’s very valuable to March Networks to have them as an
                    extension of our marketing team. They help us to really put
                    our best foot forward when it comes to our website, digital
                    products, and digital marketing.”
                  </p>
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </LightMode>
    </Layout>
  );
};

export default MarchNetworks;
